import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowRight } from "lucide-react";
import { productsService } from "@/services/productsService";
import { brandName, categoryName } from "@/lib/labels";

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 120);
    else setQ("");
  }, [open]);

  useEffect(() => {
    let active = true;
    if (!q.trim()) {
      setResults([]);
      return;
    }
    productsService.search(q).then((r) => active && setResults(r.slice(0, 6)));
    return () => (active = false);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    onClose();
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -24, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white border-b border-border shadow-lg"
          >
            <div className="container-x py-6">
              <form onSubmit={submit} className="flex items-center gap-4">
                <Search className="w-6 h-6 text-aqua shrink-0" />
                <input
                  ref={inputRef}
                  data-testid="search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search products, brands, categories…"
                  className="flex-1 text-xl md:text-2xl font-medium outline-none placeholder:text-greyish/50 bg-transparent"
                />
                <button type="button" onClick={onClose} data-testid="search-close" className="text-greyish hover:text-ink transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </form>

              {results.length > 0 && (
                <div className="mt-6 border-t border-border pt-4">
                  <p className="eyebrow mb-3">Suggestions</p>
                  <ul className="grid sm:grid-cols-2 gap-1">
                    {results.map((p) => (
                      <li key={p.id}>
                        <Link
                          to={`/products/${p.slug}`}
                          onClick={onClose}
                          data-testid={`search-suggestion-${p.slug}`}
                          className="flex items-center gap-3 p-2.5 rounded-md hover:bg-[#f6f8f9] transition-colors"
                        >
                          <img src={p.images?.[0]} alt="" loading="lazy" decoding="async" className="w-11 h-11 rounded object-cover bg-[#f6f8f9]" />
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-ink truncate">{p.name}</span>
                            <span className="block text-xs text-greyish">{brandName(p.brand)} · {categoryName(p.category)}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <button onClick={submit} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-aqua hover:gap-2.5 transition-all">
                    View all results for “{q}” <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              {q.trim() && results.length === 0 && (
                <p data-testid="search-no-suggestions" className="mt-6 text-sm text-greyish">No products match “{q}”. Press Enter to search anyway.</p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
